import { useState, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {GraduationCap, Clock, MapPin, Calendar, ExternalLink, CheckCircle2, X, Filter, Phone, Mail, Megaphone, ArrowRight, Sparkles} from 'lucide-react';
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb";
import SiloDeepDive from "@/components/SiloDeepDive";
import { formations, REGIONS, Formation } from "@/data/formations";
import type { SEOPage } from "@/data/seoPages";
import { useSeoPages } from "@/hooks/useSeoPages";
import { useFormationDepositFlow } from "@/hooks/useFormationDepositFlow";
import { lumi } from "@/lib/lumi";

// Document Lumi `organisme_formations` (subset utilisé en front public)
interface OrganismeFormationDoc {
  _id: string;
  title: string;
  category: string;
  organisme?: string;
  location: string;
  region: string;
  duration?: string;
  sessionDate: string;
  description?: string;
  programme?: string;
  prerequisites?: string;
  certification?: string;
  contact?: string;
  creator?: string;
  creatorEmail?: string;
  validationStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface CompanyProfileLite {
  creator?: string;
  raisonSociale?: string;
  telephone?: string;
  adresse?: string;
  logoUrl?: string;
}

// Type étendu localement pour porter l'email du centre (pas dans Formation natif)
type FormationWithEmail = Formation & { email?: string };

// Convertit un document `organisme_formations` validé en `Formation` UI
function organismeDocToFormation(
  d: OrganismeFormationDoc,
  company?: CompanyProfileLite
): FormationWithEmail {
  const cat = (d.category as Formation["category"]) || "BNSSA";
  const color: Formation["color"] = cat === "MNS" ? "blue" : "red";
  const organismeName =
    (company?.raisonSociale && company.raisonSociale.trim()) ||
    (d.organisme && d.organisme.trim()) ||
    "Organisme de formation";

  // Détecter un éventuel site web / téléphone / email dans le champ libre `contact`
  const contact = (d.contact || "").trim();
  const urlMatch = contact.match(/https?:\/\/[^\s]+/i);
  const emailMatch = contact.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  // On retire l'email du contact avant de chercher un téléphone, sinon le bloc
  // numérique d'un email type "jean.dupont1985@..." peut être pris pour un tél.
  const contactWithoutEmail = emailMatch
    ? contact.replace(emailMatch[0], " ")
    : contact;
  const phoneMatch = contactWithoutEmail.match(/(?:\+?\d[\d\s.\-]{7,}\d)/);

  // Conversion d'un texte libre multi-lignes en tableau de bullets
  // (saut de ligne, point-virgule ou puce comme séparateurs)
  const toBullets = (raw?: string): string[] => {
    if (!raw) return [];
    return raw
      .split(/\r?\n|;|•|·/)
      .map((s) => s.replace(/^\s*[-–—*]\s*/, "").trim())
      .filter((s) => s.length > 0);
  };

  return {
    id: `of-${d._id}`,
    title: d.title,
    category: cat,
    organisme: organismeName,
    location: d.location || "",
    region: (d.region as Formation["region"]) || "National / Multi-sites",
    duration: d.duration || "",
    price: "",
    sessionDate: d.sessionDate,
    nextSession: d.sessionDate,
    description: d.description || "",
    programme: toBullets(d.programme),
    certification:
      (d.certification && d.certification.trim()) ||
      "Certification délivrée par l'organisme",
    prerequisites: toBullets(d.prerequisites),
    color,
    website: urlMatch ? urlMatch[0] : undefined,
    phone: phoneMatch ? phoneMatch[0] : (company?.telephone || undefined),
    email: emailMatch ? emailMatch[0] : (d.creatorEmail || undefined),
    logo: company?.logoUrl || undefined,
  };
}

/**
 * Détecte une catégorie pré-filtrée par l'URL silo.
 * /bnssa/formations -> BNSSA, /maitre-nageur-sauveteur/formations -> MNS
 */
function detectSiloFromPath(pathname: string): "BNSSA" | "MNS" | null {
  if (pathname.startsWith("/bnssa/formations")) return "BNSSA";
  if (pathname.startsWith("/maitre-nageur-sauveteur/formations")) return "MNS";
  return null;
}

export default function Formations() {
  const { pathname } = useLocation();
  const lockedCategory = useMemo(() => detectSiloFromPath(pathname), [pathname]);
  const { mergedPages } = useSeoPages();

  // Flux mutualisé de dépôt de formation (pop-up coordonnées + nouvelle formation)
  const formationDeposit = useFormationDepositFlow();

  // Page SEO associée à la route silo (uniquement /bnssa/formations ou /maitre-nageur-sauveteur/formations)
  const seoPage = useMemo<SEOPage | null>(() => {
    if (!lockedCategory) return null;
    const targetPath =
      lockedCategory === "BNSSA"
        ? "/bnssa/formations"
        : "/maitre-nageur-sauveteur/formations";
    const merged = mergedPages.find((p) => p?.path === targetPath);
    if (!merged) return null;
    if (merged.isActive === false) return null;
    return merged as SEOPage;
  }, [lockedCategory, mergedPages]);

  const [category, setCategory] = useState<string>(lockedCategory ?? "");
  const [region, setRegion] = useState<string>("");
  const [selected, setSelected] = useState<FormationWithEmail | null>(null);
  const [organismeFormations, setOrganismeFormations] = useState<FormationWithEmail[]>([]);
  const [organismeIds, setOrganismeIds] = useState<Set<string>>(new Set());
  // Mobile-only : pliage du paragraphe d'intro sous le H1 (HTML toujours présent dans le DOM pour SEO)
  const [introExpanded, setIntroExpanded] = useState(false);

  // Re-synchronise la catégorie si l'URL change (navigation BNSSA <-> MNS)
  useEffect(() => {
    if (lockedCategory && category !== lockedCategory) {
      setCategory(lockedCategory);
    }
  }, [lockedCategory, category]);

  // Charge les formations validées par l'admin (collection `organisme_formations`)
  // jointes aux coordonnées de l'organisme via `company_profiles`.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [formationsRes, companiesRes] = await Promise.all([
          lumi.entities.organisme_formations
            .list({
              filter: { validationStatus: "approved" },
              sort: { sessionDate: 1 },
              limit: 1000,
              skip: 0,
            })
            .catch((err) => {
              console.error("[Formations] organisme_formations.list failed", err);
              return { list: [] as OrganismeFormationDoc[] };
            }),
          lumi.entities.company_profiles
            .list({
              sort: { updatedAt: -1 },
              limit: 1000,
              skip: 0,
            })
            .catch((err) => {
              console.error("[Formations] company_profiles.list failed", err);
              return { list: [] as CompanyProfileLite[] };
            }),
        ]);

        if (cancelled) return;

        const docs = (formationsRes.list as OrganismeFormationDoc[]) || [];
        const companies = (companiesRes.list as CompanyProfileLite[]) || [];
        const byCreator: Record<string, CompanyProfileLite> = {};
        for (const c of companies) {
          if (c?.creator) byCreator[c.creator] = c;
        }

        const mapped = docs.map((d) =>
          organismeDocToFormation(d, d.creator ? byCreator[d.creator] : undefined)
        );
        setOrganismeFormations(mapped);
        setOrganismeIds(new Set(mapped.map((m) => m.id)));
        console.log(
          `[Formations] loaded ${mapped.length} approved organisme_formations`
        );
      } catch (e) {
        console.error("[Formations] load approved formations failed", e);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  // Tri chronologique : prochaine session la plus proche en premier
  // Fusion des formations statiques + formations validées par l'admin
  const sortedFormations = useMemo(() => {
    return [...formations, ...organismeFormations].sort(
      (a, b) =>
        new Date(a.sessionDate).getTime() - new Date(b.sessionDate).getTime()
    );
  }, [organismeFormations]);

  const filtered = useMemo(() => {
    return sortedFormations.filter((f) => {
      if (category && f.category !== category) return false;
      if (region && f.region !== region) return false;
      return true;
    });
  }, [sortedFormations, category, region]);

  // Régions effectivement présentes dans les formations (statiques + organismes)
  const availableRegions = useMemo(() => {
    const set = new Set<string>();
    for (const f of formations) set.add(f.region);
    for (const f of organismeFormations) set.add(f.region);
    return REGIONS.filter((r) => set.has(r));
  }, [organismeFormations]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const reset = () => {
    // Conserve la catégorie verrouillée par l'URL (silo) si présente
    setCategory(lockedCategory ?? "");
    setRegion("");
  };

  // Le filtre verrouillé par l'URL ne compte pas comme "actif" (toujours là)
  const activeCount = [
    lockedCategory ? "" : category,
    region,
  ].filter(Boolean).length;

  // Fil d'ariane silo (uniquement sur /bnssa/formations ou /maitre-nageur-sauveteur/formations)
  const breadcrumbItems: BreadcrumbItem[] | null = useMemo(() => {
    if (!lockedCategory) return null;
    if (lockedCategory === "BNSSA") {
      return [
        { label: "BNSSA", href: "/bnssa" },
        { label: "Formations BNSSA", href: "/bnssa/formations", current: true },
      ];
    }
    return [
      { label: "Maître Nageur Sauveteur", href: "/maitre-nageur-sauveteur" },
      {
        label: "Formations Maitre nageur",
        href: "/maitre-nageur-sauveteur/formations",
        current: true,
      },
    ];
  }, [lockedCategory]);

  const breadcrumbAccent: "red" | "blue" | undefined =
    lockedCategory === "BNSSA"
      ? "red"
      : lockedCategory === "MNS"
      ? "blue"
      : undefined;

  // Titre H1 personnalisé silo
  const heroH1 =
    lockedCategory === "BNSSA"
      ? (seoPage?.h1 || "Formations BNSSA")
      : lockedCategory === "MNS"
      ? (seoPage?.h1 || "Formations Maitre nageur")
      : "Prochaines formations BNSSA & MNS";

  return (
    <Layout>
      <Seo
        title={
          seoPage?.title
            ? `${seoPage.title} | Emplois BNSSA & MNS`
            : lockedCategory === "BNSSA"
            ? "Formations BNSSA — Sessions, Recyclages PSE & Centres Agréés | Emplois BNSSA & MNS"
            : lockedCategory === "MNS"
            ? "Formations Maître Nageur — BPJEPS AAN, BEESAN, CAEPMNS | Emplois BNSSA & MNS"
            : "Formations BNSSA & MNS — BPJEPS AAN, BEESAN, Recyclages | Emplois BNSSA & MNS"
        }
        description={
          seoPage?.metaDescription
            ? seoPage.metaDescription
            : lockedCategory === "BNSSA"
            ? "Annuaire des prochaines formations BNSSA en France : préparation BNSSA, recyclages PSE1/PSE2, FC BNSSA. Sessions dispensées par les centres agréés. Filtrez par région."
            : lockedCategory === "MNS"
            ? "Annuaire des formations Maître Nageur Sauveteur : BPJEPS AAN, BEESAN, CAEPMNS, perfectionnements aquafitness. Centres agréés partout en France."
            : "Annuaire informatif des prochaines sessions de formation BNSSA, MNS, BPJEPS AAN, recyclages et perfectionnements. Centres agréés en France & DOM-TOM."
        }
        canonical={
          lockedCategory === "BNSSA"
            ? "https://bnssa-mns.fr/bnssa/formations"
            : lockedCategory === "MNS"
            ? "https://bnssa-mns.fr/maitre-nageur-sauveteur/formations"
            : "https://bnssa-mns.fr/formations"
        }
      />

      {/* Breadcrumb — bandeau blanc, même hauteur que les autres pages silo */}
      {breadcrumbItems && (
        <section className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <Breadcrumb items={breadcrumbItems} accent={breadcrumbAccent} />
          </div>
        </section>
      )}

      {/* CTA — Espace organisme de formation (remonté au-dessus du hero) */}
      <section className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Megaphone className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-sm sm:text-base">
                Vous êtes un organisme de formation ?
              </p>
              <p className="text-xs sm:text-sm text-amber-50 mt-0.5">
                Créez gratuitement votre compte organisme et publiez vos
                sessions BNSSA, BPJEPS AAN, BEESAN, recyclages…
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={formationDeposit.openModal}
            className="inline-flex items-center gap-2 bg-white text-amber-700 px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-amber-50 transition-colors shadow-lg whitespace-nowrap"
          >
            Annoncer une formation
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Hero — fond blanc, écritures noires, accents silo (rouge BNSSA / bleu MNS) */}
      <section className="bg-white border-b border-gray-200 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex items-center gap-2 mb-4 ${
              lockedCategory === "BNSSA"
                ? "text-red-600"
                : lockedCategory === "MNS"
                ? "text-blue-600"
                : "text-gray-700"
            }`}
          >
            <GraduationCap className="w-7 h-7" />
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider">
              Annuaire informatif
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {heroH1}
          </h1>
          {/* Signature visuelle : fine barre dégradée rouge→bleu sous le H1 */}
          <div className="h-[3px] w-24 rounded-full bg-gradient-to-r from-red-600 via-fuchsia-500 to-blue-600 mb-5" />
          {/* Bloc d'intro tronqué en mobile (lg:hidden), full visible en desktop.
              Le HTML reste TOUJOURS dans le DOM pour préserver le SEO. */}
          <div className="max-w-3xl text-gray-700">
            <p
              className={`text-sm sm:text-base lg:text-lg leading-snug lg:leading-relaxed overflow-hidden transition-[max-height] duration-300 ease-out ${
                introExpanded
                  ? "max-h-[2000px]"
                  : "max-h-[5rem] lg:max-h-none"
              } ${
                introExpanded
                  ? ""
                  : "[mask-image:linear-gradient(to_bottom,black_55%,transparent)] lg:[mask-image:none]"
              }`}
            >
            {lockedCategory === "BNSSA" ? (
              <>
                Retrouvez sur cette page l'annuaire des prochaines formations
                BNSSA proposées partout en France par les centres agréés.{" "}
                <strong className="text-gray-900">Emplois BNSSA &amp; MNS</strong>{" "}
                centralise les sessions de formation initiale BNSSA, de
                recyclage PSE1/PSE2 et de révision quinquennale FC BNSSA
                publiées par les organismes partenaires (FFSS, Croix-Rouge,
                Protection Civile, SDIS, écoles privées). Filtrez les sessions
                par région, comparez les centres et contactez directement
                l'organisme de votre choix pour vous inscrire — la plateforme
                reste{" "}
                <strong className="text-gray-900">strictement informative</strong>.
              </>
            ) : lockedCategory === "MNS" ? (
              <>
                Retrouvez sur cette page l'annuaire des prochaines formations
                Maître Nageur Sauveteur organisées partout en France par les
                centres agréés.{" "}
                <strong className="text-gray-900">Emplois BNSSA &amp; MNS</strong>{" "}
                centralise les sessions de BPJEPS AAN (Activités Aquatiques et
                de la Natation), de BEESAN, de CAEPMNS (recyclage quinquennal
                obligatoire), de recyclage PSE1 annuel et de perfectionnements
                thématiques (bébés nageurs, aquafitness, aquaphobie, sauvetage
                côtier). Filtrez par région, comparez les centres et contactez
                directement l'organisme de votre choix — la plateforme reste{" "}
                <strong className="text-gray-900">strictement informative</strong>.
              </>
            ) : (
              <>
                Retrouvez ici les prochaines sessions de formation organisées
                par les centres agréés partout en France. Cette page est{" "}
                <strong className="text-gray-900">strictement informative</strong>{" "}
                — les inscriptions se font directement auprès de l'organisme
                formateur.
              </>
            )}
            </p>
            <button
              type="button"
              onClick={() => setIntroExpanded((v) => !v)}
              aria-expanded={introExpanded}
              className={`lg:hidden mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider underline underline-offset-2 hover:opacity-80 ${
                lockedCategory === "BNSSA"
                  ? "text-red-600"
                  : lockedCategory === "MNS"
                  ? "text-blue-600"
                  : "text-gray-900"
              }`}
            >
              {introExpanded ? "Lire moins" : "Lire plus"}
            </button>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          {/* Catégorie */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mr-2">
              <Filter className="w-4 h-4" />
              Catégorie :
            </span>
            {[
              { value: "", label: "Toutes" },
              { value: "BNSSA", label: "BNSSA" },
              { value: "MNS", label: "MNS" },
              { value: "Recyclage", label: "Recyclage" },
              { value: "Perfectionnement", label: "Perfectionnement" },
            ].map((opt) => {
              const disabled = lockedCategory !== null && opt.value !== lockedCategory;
              return (
                <button
                  key={opt.value}
                  onClick={() => !disabled && setCategory(opt.value)}
                  aria-pressed={category === opt.value}
                  disabled={disabled}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    category === opt.value
                      ? "bg-gray-900 text-white"
                      : disabled
                      ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
            {lockedCategory && (
              <span className="text-xs text-gray-500 italic ml-1">
                (verrouillé sur l'univers {lockedCategory})
              </span>
            )}
          </div>

          {/* Région */}
          <div className="flex items-center gap-2 flex-wrap">
            <label
              htmlFor="region-filter"
              className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mr-2"
            >
              <MapPin className="w-4 h-4" />
              Région :
            </label>
            <select
              id="region-filter"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-full text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-amber-500 outline-none"
            >
              <option value="">Toutes les régions</option>
              {availableRegions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>

            {activeCount > 0 && (
              <button
                onClick={reset}
                className="ml-auto text-xs text-red-600 hover:underline flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Catalog */}
      <section className="py-12 bg-gray-50 min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between mb-6">
            <p className="text-sm text-gray-600">
              {filtered.length} session{filtered.length > 1 ? "s" : ""} à venir
              {region && ` en ${region}`}
              {category && ` — ${category}`}
            </p>
            <p className="text-xs text-gray-500 hidden sm:block">
              Triées de la plus proche à la plus lointaine
            </p>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Aucune session à venir
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Aucun centre ne propose actuellement de session correspondant à
                ces filtres.
              </p>
              <button
                onClick={reset}
                className="text-sm text-amber-700 hover:underline font-medium"
              >
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((f: FormationWithEmail) => {
                const accentBg = f.color === "red" ? "bg-red-600" : "bg-blue-600";
                const accentBgLight =
                  f.color === "red" ? "bg-red-50" : "bg-blue-50";
                const accentBorder =
                  f.color === "red" ? "border-red-200" : "border-blue-200";

                const isFromOrganisme = organismeIds.has(f.id);
                return (
                  <article
                    key={f.id}
                    className={`bg-white border ${accentBorder} rounded-xl overflow-hidden hover:shadow-lg transition-shadow flex flex-col`}
                  >
                    <div className={`${accentBgLight} p-5 border-b ${accentBorder}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block px-2 py-0.5 ${accentBg} text-white text-xs font-bold rounded-md`}
                          >
                            {f.category}
                          </span>
                          {isFromOrganisme && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-md uppercase tracking-wider">
                              <Sparkles className="w-3 h-3" />
                              Nouvelle
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-semibold text-gray-700">
                          {f.region}
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        {f.logo && (
                          <img
                            src={f.logo}
                            alt={`Logo ${f.organisme}`}
                            className="w-12 h-12 rounded-md object-contain bg-white border border-gray-200 flex-shrink-0"
                            loading="lazy"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                            }}
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="text-lg font-bold text-gray-900 leading-snug mb-1">
                            {f.title}
                          </h3>
                          <p className="text-xs text-gray-600 truncate">{f.organisme}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-5 flex-1 flex flex-col">
                      <ul className="space-y-2 mb-5 text-sm">
                        <li className="flex items-center gap-2 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-semibold">
                            {formatDate(f.sessionDate)}
                          </span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span>{f.duration}</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{f.location}</span>
                        </li>
                      </ul>

                      <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                        {f.description}
                      </p>

                      <button
                        onClick={() => setSelected(f)}
                        className={`mt-auto w-full ${accentBg} hover:opacity-90 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-opacity flex items-center justify-center gap-2`}
                      >
                        Voir la fiche & contacter
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* SEO content (silo only) — Intro + sections sous les fiches */}
      {seoPage?.intro && (
        <section className="bg-white py-12 border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className={`seo-rich-content text-gray-800 leading-relaxed text-base sm:text-lg ${
                lockedCategory === "BNSSA" ? "seo-rich-red" : "seo-rich-blue"
              }`}
              dangerouslySetInnerHTML={{
                __html: /<\/?[a-z][\s\S]*>/i.test(seoPage.intro)
                  ? seoPage.intro
                  : `<p>${seoPage.intro
                      .split(/\n\s*\n/)
                      .map((p) => p.trim())
                      .filter(Boolean)
                      .join("</p><p>")}</p>`,
              }}
            />
          </div>
        </section>
      )}

      {seoPage?.sections && seoPage.sections.length > 0 && (
        <section className="bg-gray-50 py-12 border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className={`text-2xl sm:text-3xl font-bold mb-8 ${
                lockedCategory === "BNSSA" ? "text-red-700" : "text-blue-700"
              }`}
            >
              {seoPage.keyword
                ? `Tout savoir sur les ${seoPage.keyword}`
                : "En savoir plus"}
            </h2>
            <div className="space-y-10">
              {seoPage.sections.map((section, i) => {
                const cleanTitle = (section.title || "").replace(/&nbsp;/g, " ");
                return (
                  <article key={i}>
                    <h3
                      className={`text-xl sm:text-2xl font-bold mb-3 ${
                        lockedCategory === "BNSSA"
                          ? "text-red-800"
                          : "text-blue-800"
                      }`}
                      dangerouslySetInnerHTML={{ __html: cleanTitle }}
                    />
                    <div
                      className={`seo-rich-content text-gray-700 leading-relaxed text-base ${
                        lockedCategory === "BNSSA"
                          ? "seo-rich-red"
                          : "seo-rich-blue"
                      }`}
                      dangerouslySetInnerHTML={{
                        __html: /<\/?[a-z][\s\S]*>/i.test(section.content)
                          ? section.content
                          : `<p>${section.content
                              .split(/\n\s*\n/)
                              .map((p) => p.trim())
                              .filter(Boolean)
                              .join("</p><p>")}</p>`,
                      }}
                    />
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ─── APPROFONDIR LE GUIDE (silo BNSSA / MNS uniquement) ─────────────── */}
      {lockedCategory && (
        <SiloDeepDive
          silo={lockedCategory}
          currentPath={
            lockedCategory === "BNSSA"
              ? "/bnssa/formations"
              : "/maitre-nageur-sauveteur/formations"
          }
        />
      )}

      {/* Modal — Fiche détaillée informative */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="formation-title"
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between z-10">
              <h2 id="formation-title" className="text-lg font-bold text-gray-900">
                Fiche formation
              </h2>
              <button
                onClick={() => setSelected(null)}
                aria-label="Fermer"
                className="p-1.5 hover:bg-gray-100 rounded-md"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div
                className={`p-4 ${
                  selected.color === "red"
                    ? "bg-red-50 border-red-200"
                    : "bg-blue-50 border-blue-200"
                } border rounded-lg`}
              >
                <div className="flex items-start gap-3 mb-3">
                  {selected.logo && (
                    <img
                      src={selected.logo}
                      alt={`Logo ${selected.organisme}`}
                      className="w-16 h-16 rounded-lg object-contain bg-white border border-gray-200 flex-shrink-0"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                      }}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">
                      {selected.title}
                    </h3>
                    <p className="text-xs text-gray-600">
                      Organisé par <strong>{selected.organisme}</strong>
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                  <div>
                    <strong>Date :</strong> {formatDate(selected.sessionDate)}
                  </div>
                  <div>
                    <strong>Durée :</strong> {selected.duration}
                  </div>
                  <div>
                    <strong>Région :</strong> {selected.region}
                  </div>
                  <div>
                    <strong>Lieu :</strong> {selected.location}
                  </div>
                  <div className="col-span-2">
                    <strong>Session :</strong> {selected.nextSession}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                  Description
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {selected.description}
                </p>
              </div>

              {selected.programme.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                    Programme
                  </h4>
                  <ul className="space-y-1">
                    {selected.programme.map((p, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-gray-700"
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selected.prerequisites.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                    Prérequis
                  </h4>
                  <ul className="space-y-1">
                    {selected.prerequisites.map((p, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        • {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                  Certification délivrée
                </h4>
                <p className="text-sm text-gray-700">{selected.certification}</p>
              </div>

              {/* Bloc contact direct centre */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">
                  Inscription auprès du centre
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  Pour vous inscrire à cette session, contactez directement{" "}
                  <strong>{selected.organisme}</strong> via les coordonnées
                  ci-dessous.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                  {selected.website && (
                    <a
                      href={selected.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-semibold"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Site du centre
                    </a>
                  )}
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone.replace(/\s/g, "")}`}
                      className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 hover:bg-gray-100 text-gray-900 rounded-lg text-sm font-semibold"
                    >
                      <Phone className="w-4 h-4" />
                      {selected.phone}
                    </a>
                  )}
                  {selected.email && (
                    <a
                      href={`mailto:${selected.email}?subject=${encodeURIComponent(
                        `Demande d'inscription — ${selected.title}`
                      )}`}
                      className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 hover:bg-gray-100 text-gray-900 rounded-lg text-sm font-semibold break-all"
                    >
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{selected.email}</span>
                    </a>
                  )}
                </div>
                <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                  bnssa-mns.fr ne traite aucune inscription et ne perçoit aucun
                  frais. Vérifiez les disponibilités et conditions tarifaires
                  directement auprès de l'organisme.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pop-up de dépôt de formation (coordonnées + nouvelle formation) */}
      {formationDeposit.modal}
    </Layout>
  );
}
